import { keyframes } from '@emotion/react';
import { ReadonlyVec3 } from 'gl-matrix';
import React, { useCallback, useRef } from 'react';
import { Box, styled } from '@mui/material';
import { dominantComponent } from '~/domain/geometry/patientGeometry';
import { VoiShape } from '~/domain/services/imageService/gridGeometry';
import { VoiBounds } from '~/state/atoms/toolsAtoms';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';

/**
 * Corner-handle edge length, px. The corners are square and all four are
 * laid out from this one number (see {@link CORNER_OFFSET_PX}) — no
 * per-corner width, height or transform, so they cannot drift apart.
 */
const HANDLE_SIZE_PX = 10;

/**
 * Offset that centres a corner handle on its corner: half the handle
 * sticks out of the rect on each axis. Written as a negative inset
 * rather than `inset: 0` + a percentage transform, so the four corners
 * are literally the same magnitude and the layout is inspectable in the
 * DOM.
 */
const CORNER_OFFSET_PX = -HANDLE_SIZE_PX / 2;

/**
 * Thickness of the invisible edge-grab bars, px. Centred on the border,
 * so each bar reaches EDGE_THICKNESS_PX / 2 either side of it.
 */
const EDGE_THICKNESS_PX = 6;

const ProjectedVoiBoxHandle = styled(Box)`
    position: absolute;
    width: ${HANDLE_SIZE_PX}px;
    height: ${HANDLE_SIZE_PX}px;
    background-color: ${({ theme }) => theme.palette.secondary.main};
    border: 1px solid ${({ theme }) => theme.palette.secondary.light};
    box-sizing: border-box;
    z-index: 6;
    pointer-events: auto;
`;

/**
 * Activation emphasis: a brief glow that fades out, so the box is
 * noticed at the moment a consumer activates the overlay — including a
 * box restored from the session record on a panel reopen. Purely
 * presentational — bounds and drag math are untouched.
 */
const voiActivationPulse = keyframes`
    0% {
        box-shadow: 0 0 0 4px rgba(244, 152, 0, 0.9),
            inset 0 0 12px rgba(244, 152, 0, 0.7);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(244, 152, 0, 0),
            inset 0 0 0 rgba(244, 152, 0, 0);
    }
`;

/**
 * Visual inset of the border stroke, in px. ZERO, so the stroke's outer
 * edge IS the region's boundary and the fill covers exactly what the
 * segmentation will cut to. Painting it inside the rect — which this did
 * — makes the drawn region smaller than the real one, and the applied
 * structure then reads as overshooting an edge it never crossed. The
 * clip window is grown by half a handle either way (see `grownLeft`
 * below), so the stroke has room to paint even with the rect against the
 * viewport edge.
 */
const BORDER_INSET_PX = 0;

/**
 * Opacity of the off-slab outline — the measurement overlay's off-plane
 * value, so "this exists but is not on the displayed slice" reads the
 * same across the viewport overlays.
 */
const OFF_SLAB_OPACITY = 0.4;

enum DragAxis {
    X = 'X',
    Y = 'Y',
    Z = 'Z',
}

enum HandleType {
    MOVE = 'move',
    TOP_LEFT = 'top-left',
    TOP_RIGHT = 'top-right',
    BOTTOM_LEFT = 'bottom-left',
    BOTTOM_RIGHT = 'bottom-right',
    TOP = 'top',
    BOTTOM = 'bottom',
    LEFT = 'left',
    RIGHT = 'right',
}

interface DragState {
    type: HandleType;
    startMouse: { x: number; y: number };
    startVoi: VoiBounds;
}

interface ProjectedVoiBoxProps {
    rect: { left: number; top: number; width: number; height: number };
    voi: VoiBounds;
    setVoi: (v: VoiBounds) => void;
    view: PlaneView;
    /**
     * Increments once per consumer activation (see VoiSelectorOverlay);
     * keys the border layer so the activation pulse replays exactly then
     * — never on the re-renders every drag produces. 0 = no pulse.
     */
    activationPulseToken: number;
    /**
     * Whether the view's current slice cuts the box's slab (see
     * {@link isVoiBoxOnCurrentSlice}). On the slab the full solid box
     * with its nine grab targets renders; off it only a dimmed, dashed,
     * NON-interactive outline does — visible so a user scrolled away can
     * still find the restriction and scroll back to it, non-interactive
     * so no drag can start on a slice the box does not lie on and the
     * pointer reaches the canvas underneath.
     */
    interactive: boolean;
    /**
     * Body the bounds select. `'box'` draws the bounds themselves;
     * `'ellipsoid'` draws the inscribed ellipsoid's cross-section on this
     * slice and demotes the bounds outline to the handle frame it is.
     * The handles, the drag write-back and the slab rule are identical
     * for both — only the shape drawn inside the rect differs.
     */
    shape: VoiShape;
    /**
     * Cross-section size of the ellipsoid on this slice, relative to the
     * bounds ({@link ellipsoidCrossSectionScale}). Ignored for a box.
     */
    ellipsoidScale: number;
}

/**
 * Smallest ellipse the overlay draws, in CSS px. The cross-section goes
 * to zero at the ellipsoid's poles, and a zero-size element is simply
 * invisible — the user would lose the region on the very slices its own
 * extent ends at. A visibility floor on the DRAWING only: the mask, the
 * clamp and the statistics use the geometry, never this.
 */
const MIN_ELLIPSE_PX = 4;

/**
 * Inset placing the ellipse concentric in the projected rect at `scale`
 * of its size, floored at {@link MIN_ELLIPSE_PX} so a polar slice still
 * shows where the region is.
 */
function ellipseInsetSx(
    rect: { width: number; height: number },
    scale: number
): { left: number; top: number; width: number; height: number } {
    const width = Math.max(MIN_ELLIPSE_PX, rect.width * scale);
    const height = Math.max(MIN_ELLIPSE_PX, rect.height * scale);
    return {
        left: (rect.width - width) / 2,
        top: (rect.height - height) / 2,
        width,
        height,
    };
}

const ensureValidBounds = (voi: VoiBounds): VoiBounds => ({
    minX: Math.min(voi.minX, voi.maxX),
    maxX: Math.max(voi.minX, voi.maxX),
    minY: Math.min(voi.minY, voi.maxY),
    maxY: Math.max(voi.minY, voi.maxY),
    minZ: Math.min(voi.minZ, voi.maxZ),
    maxZ: Math.max(voi.minZ, voi.maxZ),
});

/**
 * The world axis a screen-space direction predominantly runs along, and
 * whether it points toward that axis' positive end.
 *
 * The resize write-back used to hard-code the HFS view→patient map per
 * orientation label (transversal: X right / Y down, sagittal: −Y right / Z up,
 * …). The true mapping is a property of the VIEW, not the label:
 * {@link PlaneView.computeViewOrientation} bakes the image orientation
 * into each view's basis, so on an HFP/FFS/FFP series (a routine PET
 * patient position) every flipped axis made a handle write the OPPOSITE
 * bound — dragging one edge visibly moved the other, and the limits set
 * in one viewport landed elsewhere than placed when checked in another.
 * Reading the axis and sign off the view's own basis keeps every handle
 * glued to its edge on every orientation, oblique frames included (the
 * box itself stays patient-axis-aligned, so the DOMINANT axis is the
 * only meaningful choice there).
 */
function dominantWorldAxis(direction: ReadonlyVec3): {
    axis: DragAxis;
    positive: boolean;
} {
    const { index } = dominantComponent(direction);
    return {
        axis: [DragAxis.X, DragAxis.Y, DragAxis.Z][index],
        positive: direction[index] > 0,
    };
}

const oppositeSign = (sign: 1 | -1): 1 | -1 => (sign === 1 ? -1 : 1);

export default function ProjectedVoiBoxWithHandles({
    rect,
    voi,
    setVoi,
    view,
    activationPulseToken,
    interactive,
    shape,
    ellipsoidScale,
}: ProjectedVoiBoxProps) {
    const dragRef = useRef<DragState | null>(null);

    // Clip window = the projected rect grown by one handle radius (so no
    // handle is sliced in half by the overflow, which is what made the
    // four corners look different from one another), intersected with the
    // viewport. The intersection is taken on the box's ACTUAL edges —
    // `left + width`, not `width` — so a box hanging off the left edge
    // does not report a window wider than itself.
    //
    // When the canvas reports no size at all (not laid out yet, or in a
    // hidden viewport) the intersection is SKIPPED rather than applied:
    // clamping against a zero-sized viewport collapses the window and the
    // whole box vanishes. An unclipped box is the fail-visible outcome;
    // a silently missing restriction indicator is not.
    const viewportRect = view.canvas?.getBoundingClientRect();
    const viewportWidth = viewportRect?.width ?? 0;
    const viewportHeight = viewportRect?.height ?? 0;
    const hasViewportBounds = viewportWidth > 0 && viewportHeight > 0;

    const grownLeft = rect.left - HANDLE_SIZE_PX / 2;
    const grownTop = rect.top - HANDLE_SIZE_PX / 2;
    const grownRight = rect.left + rect.width + HANDLE_SIZE_PX / 2;
    const grownBottom = rect.top + rect.height + HANDLE_SIZE_PX / 2;

    const visibleLeft = hasViewportBounds ? Math.max(grownLeft, 0) : grownLeft;
    const visibleTop = hasViewportBounds ? Math.max(grownTop, 0) : grownTop;
    const visibleRight = hasViewportBounds
        ? Math.min(grownRight, viewportWidth)
        : grownRight;
    const visibleBottom = hasViewportBounds
        ? Math.min(grownBottom, viewportHeight)
        : grownBottom;

    const visibleWidth = Math.max(visibleRight - visibleLeft, 0);
    const visibleHeight = Math.max(visibleBottom - visibleTop, 0);
    const offsetX = rect.left - visibleLeft;
    const offsetY = rect.top - visibleTop;

    const moveAxis = (
        voiBounds: VoiBounds,
        axis: DragAxis,
        sign: 1 | -1,
        delta: { x: number; y: number; z: number }
    ): VoiBounds => {
        const deltaValue = delta[axis.toLowerCase() as 'x' | 'y' | 'z'];
        const key = (
            sign === 1 ? `max${axis}` : `min${axis}`
        ) as keyof VoiBounds;
        return { ...voiBounds, [key]: voiBounds[key] + deltaValue };
    };

    const onDrag = useCallback(
        (e: MouseEvent) => {
            if (!dragRef.current) {
                return;
            }
            const { type, startMouse, startVoi } = dragRef.current;

            const dx = e.clientX - startMouse.x;
            const dy = e.clientY - startMouse.y;

            const startPatientCoords = view.patientCoordsFromCanvasCoords(
                startMouse.x,
                startMouse.y
            );
            const currentPatientCoords = view.patientCoordsFromCanvasCoords(
                startMouse.x + dx,
                startMouse.y + dy
            );

            const delta = {
                x: currentPatientCoords[0] - startPatientCoords[0],
                y: currentPatientCoords[1] - startPatientCoords[1],
                z: currentPatientCoords[2] - startPatientCoords[2],
            };

            let newVoi = { ...startVoi };

            if (type === HandleType.MOVE) {
                Object.values(DragAxis).forEach((axis) => {
                    newVoi = moveAxis(newVoi, axis, 1, delta);
                    newVoi = moveAxis(newVoi, axis, -1, delta);
                });
            } else {
                // Screen-right runs along +viewXDirection and screen-up
                // along +viewYDirection (canvas y is flipped inside
                // canvasCoordsFromPatientCoords), so the LEFT edge sits
                // at the LOW end of the horizontal world axis exactly
                // when that axis points screen-right — and mirrored when
                // it points left; same reasoning vertically. See
                // {@link dominantWorldAxis} for why this is derived from
                // the view instead of the orientation label.
                const horizontal = dominantWorldAxis(view.viewXDirection);
                const vertical = dominantWorldAxis(view.viewYDirection);
                const leftSign: 1 | -1 = horizontal.positive ? -1 : 1;
                const topSign: 1 | -1 = vertical.positive ? 1 : -1;

                if (
                    type === HandleType.LEFT ||
                    type === HandleType.TOP_LEFT ||
                    type === HandleType.BOTTOM_LEFT
                ) {
                    newVoi = moveAxis(newVoi, horizontal.axis, leftSign, delta);
                }
                if (
                    type === HandleType.RIGHT ||
                    type === HandleType.TOP_RIGHT ||
                    type === HandleType.BOTTOM_RIGHT
                ) {
                    newVoi = moveAxis(
                        newVoi,
                        horizontal.axis,
                        oppositeSign(leftSign),
                        delta
                    );
                }
                if (
                    type === HandleType.TOP ||
                    type === HandleType.TOP_LEFT ||
                    type === HandleType.TOP_RIGHT
                ) {
                    newVoi = moveAxis(newVoi, vertical.axis, topSign, delta);
                }
                if (
                    type === HandleType.BOTTOM ||
                    type === HandleType.BOTTOM_LEFT ||
                    type === HandleType.BOTTOM_RIGHT
                ) {
                    newVoi = moveAxis(
                        newVoi,
                        vertical.axis,
                        oppositeSign(topSign),
                        delta
                    );
                }
            }

            setVoi(ensureValidBounds(newVoi));
        },
        [setVoi, view]
    );

    const onEnd = useCallback(() => {
        dragRef.current = null;
    }, []);

    /**
     * Forward a double-click on the box's INTERIOR to the view's canvas.
     * The grab targets are DOM siblings of the canvas, and the VOI
     * overlay's recenter listener lives on the canvas: a double-click
     * aimed inside the box (the most natural place to aim it) reached
     * nothing at all.
     *
     * Only the MOVE region forwards. The edge bars and corner squares
     * are resize grab targets: a recenter fired from one would compete
     * with the resize gesture the same press started, and there is no
     * unambiguous reading of "double-click the handle you are dragging".
     * A double-click there is inert, as before.
     *
     * The canvas listener is the one place the recenter is decided, so a
     * consumer that does not register it (registration) stays inert here
     * too — this forwards an event, it does not act on the bounds.
     */
    const forwardDoubleClickToCanvas = useCallback(
        (event: React.MouseEvent) => {
            view.canvas?.dispatchEvent(
                new MouseEvent('dblclick', {
                    bubbles: true,
                    cancelable: true,
                    clientX: event.clientX,
                    clientY: event.clientY,
                    screenX: event.screenX,
                    screenY: event.screenY,
                    button: event.button,
                    buttons: event.buttons,
                    ctrlKey: event.ctrlKey,
                    shiftKey: event.shiftKey,
                    altKey: event.altKey,
                    metaKey: event.metaKey,
                })
            );
        },
        [view]
    );

    const startDrag = useWindowMouseDrag(onDrag, onEnd);

    const beginDrag = (e: React.MouseEvent, type: HandleType) => {
        dragRef.current = {
            type,
            startMouse: { x: e.clientX, y: e.clientY },
            startVoi: { ...voi },
        };
        startDrag(e);
    };

    // Handle layout, all four corners and all four edges derived from the
    // same two constants so no corner can differ from another.
    //
    // The corner squares occupy HANDLE_SIZE_PX / 2 either side of their
    // corner; the edge bars are inset by exactly that much, so bar and
    // square butt together with neither a dead gap nor an overlap. The
    // MOVE region starts where the bars end (EDGE_THICKNESS_PX / 2), so
    // the whole rect is covered by exactly one grab target everywhere.
    const cornerInsetPx = HANDLE_SIZE_PX / 2;
    const edgeHalfPx = EDGE_THICKNESS_PX / 2;
    const invisible = {
        background: 'transparent',
        border: 'none',
    } as const;
    const horizontalBar = {
        left: cornerInsetPx,
        right: cornerInsetPx,
        height: EDGE_THICKNESS_PX,
        width: 'auto',
        cursor: 'ns-resize',
        ...invisible,
    } as const;
    const verticalBar = {
        top: cornerInsetPx,
        bottom: cornerInsetPx,
        width: EDGE_THICKNESS_PX,
        height: 'auto',
        cursor: 'ew-resize',
        ...invisible,
    } as const;

    const handles = [
        // Full interior for move (rendered first, behind edges/corners)
        {
            type: HandleType.MOVE,
            style: {
                left: edgeHalfPx,
                right: edgeHalfPx,
                top: edgeHalfPx,
                bottom: edgeHalfPx,
                width: 'auto',
                height: 'auto',
                cursor: 'move',
                ...invisible,
            },
        },
        // Edge handles (invisible bars along each border)
        {
            type: HandleType.TOP,
            style: { ...horizontalBar, top: -edgeHalfPx },
        },
        {
            type: HandleType.BOTTOM,
            style: { ...horizontalBar, bottom: -edgeHalfPx },
        },
        {
            type: HandleType.LEFT,
            style: { ...verticalBar, left: -edgeHalfPx },
        },
        {
            type: HandleType.RIGHT,
            style: { ...verticalBar, right: -edgeHalfPx },
        },
        // Corner handles: same offset magnitude on every corner, no
        // percentage transform (which is what left the four corners
        // visually inconsistent once the overflow clip cut into them).
        {
            type: HandleType.TOP_LEFT,
            style: {
                left: CORNER_OFFSET_PX,
                top: CORNER_OFFSET_PX,
                cursor: 'nwse-resize',
            },
        },
        {
            type: HandleType.TOP_RIGHT,
            style: {
                right: CORNER_OFFSET_PX,
                top: CORNER_OFFSET_PX,
                cursor: 'nesw-resize',
            },
        },
        {
            type: HandleType.BOTTOM_LEFT,
            style: {
                left: CORNER_OFFSET_PX,
                bottom: CORNER_OFFSET_PX,
                cursor: 'nesw-resize',
            },
        },
        {
            type: HandleType.BOTTOM_RIGHT,
            style: {
                right: CORNER_OFFSET_PX,
                bottom: CORNER_OFFSET_PX,
                cursor: 'nwse-resize',
            },
        },
    ];

    return (
        <Box
            data-testid="voi-box"
            data-voi-interactive={interactive ? 'true' : 'false'}
            sx={{
                position: 'absolute',
                left: visibleLeft,
                top: visibleTop,
                width: visibleWidth,
                height: visibleHeight,
                overflow: 'hidden',
                pointerEvents: 'none',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    left: offsetX,
                    top: offsetY,
                    width: rect.width,
                    height: rect.height,
                    zIndex: 5,
                    pointerEvents: 'none',
                }}
            >
                {/* Inset visual stroke + fill; a non-interactive layer so
                    the handles keep the true rect geometry. Off the slab
                    it dims to a dashed, unfilled outline (the POI /
                    measurement off-plane idiom): still findable when the
                    user has scrolled away from the box, but no longer
                    presenting as a restriction of the visible slice. */}
                <Box
                    key={activationPulseToken}
                    data-testid="voi-region-outline"
                    data-voi-shape={shape}
                    sx={(theme) => ({
                        position: 'absolute',
                        ...(shape === 'ellipsoid'
                            ? ellipseInsetSx(rect, ellipsoidScale)
                            : { inset: `${BORDER_INSET_PX}px` }),
                        borderRadius: shape === 'ellipsoid' ? '50%' : 0,
                        border: `2px ${interactive ? 'solid' : 'dashed'} ${
                            theme.palette.secondary.main
                        }`,
                        background: interactive
                            ? 'rgba(244, 152, 0, 0.15)'
                            : 'none',
                        opacity: interactive ? 1 : OFF_SLAB_OPACITY,
                        pointerEvents: 'none',
                        animation:
                            activationPulseToken > 0
                                ? `${voiActivationPulse} 1.4s ease-out`
                                : 'none',
                    })}
                />
                {/* The bounds still frame the handles, so under an
                    ellipsoid they stay drawn — faintly, as the frame they
                    are rather than as the region, which the ellipse is. */}
                {shape === 'ellipsoid' && (
                    <Box
                        data-testid="voi-bounds-frame"
                        sx={(theme) => ({
                            position: 'absolute',
                            inset: `${BORDER_INSET_PX}px`,
                            border: `1px dashed ${theme.palette.secondary.main}`,
                            opacity: OFF_SLAB_OPACITY,
                            pointerEvents: 'none',
                        })}
                    />
                )}
                {/* No grab targets off the slab: a drag must not be able
                    to start on a slice the box does not lie on. The
                    window listeners of a drag already in progress live
                    on this component, not on the handles, so a
                    mid-gesture flip cannot cut a started drag short. */}
                {interactive &&
                    handles.map(({ type, style }) => (
                        <ProjectedVoiBoxHandle
                            key={type}
                            data-testid={`voi-handle-${type}`}
                            onMouseDown={(e) => beginDrag(e, type)}
                            onDoubleClick={
                                type === HandleType.MOVE
                                    ? forwardDoubleClickToCanvas
                                    : undefined
                            }
                            style={style}
                        />
                    ))}
            </Box>
        </Box>
    );
}
